import Decimal from 'decimal.js';
import { timeToMin, vnDateOf, vnMinuteOfDay } from './attendance';
import { DEFAULT_DRIVER_RULES, floorTo, nightTierPay, nightTiersOn, type DriverRuleParams } from './driver-rules';

/**
 * P21 B4b – chấm công + tăng ca tài xế, chép quy tắc từ minhtiencrm (driver-attendance.service.js, driver-payroll.service.js).
 * Hàm thuần, giờ VN. Khác CRM: chưa có khoản "hỗ trợ đơn lấy hàng" (ERP chưa có loại đơn lấy hàng) và
 * "lệnh tăng ca đã duyệt" (chờ B3); phụ cấp phụ xe giao đêm trả cho chính người phụ xe.
 */
export interface DriverPolicy {
  gateInOpen: string;
  earlyOtUntil: string;
  onTimeUntil: string;
  gateInClose: string;
  dayStart: string;
  dayEnd: string;
  depotLat: number | null;
  depotLng: number | null;
  depotRadiusM: number;
  /** Nơi làm việc có tọa độ (DEC-388): chưa khai bãi riêng thì chấm trong bán kính nơi nào cũng tính là ở bãi */
  sites?: { lat: number; lng: number; radiusM: number }[];
  requireDepotForOt: boolean;
  earlyOtRate: number;
  earlyOtMinMinutes: number;
  latePenaltyRate: number;
  lunchStart: string;
  lunchEnd: string;
  lunchMinKm: number;
  lunchRate: number;
  after17Start: string;
  after17Rate: number;
  nightStart: string;
  nightEnd: string;
  nightRate: number;
  nightAssistantRate: number;
  /** P21 (0083): cổng chấm ra của tài xế; null = không chặn */
  gateOutOpen: string | null;
  gateOutClose: string | null;
  /** Tăng ca sáng phải có xe chạy ít nhất X km (GPS) từ lúc vào tới hạn tăng ca sáng – như minhtiencrm */
  earlyOtMinKm: number;
  /** Ngày công chuẩn tháng của tài xế (tính lương) */
  standardDays: number;
}

export const DEFAULT_DRIVER_POLICY: DriverPolicy = {
  gateInOpen: '04:00',
  earlyOtUntil: '07:00',
  onTimeUntil: '07:15',
  gateInClose: '08:00',
  dayStart: '04:00',
  dayEnd: '21:00',
  depotLat: null,
  depotLng: null,
  depotRadiusM: 200,
  requireDepotForOt: true,
  earlyOtRate: 60000,
  earlyOtMinMinutes: 15,
  latePenaltyRate: 50000,
  lunchStart: '11:30',
  lunchEnd: '13:15',
  lunchMinKm: 25,
  lunchRate: 60000,
  after17Start: '17:00',
  after17Rate: 50000,
  nightStart: '21:00',
  nightEnd: '23:00',
  nightRate: 450000,
  nightAssistantRate: 350000,
  gateOutOpen: null,
  gateOutClose: null,
  earlyOtMinKm: 2,
  standardDays: 22,
};

export const DRIVER_PAY_LABELS = {
  early: 'Tăng ca sáng',
  lunch: 'Chạy xuyên trưa',
  after17: 'Tăng ca sau 17h',
  night: 'Hỗ trợ đêm',
  night_assistant: 'Phụ xe hỗ trợ đêm',
  after_midnight: 'Hỗ trợ sau 0h',
  late: 'Trừ đi trễ',
  forgot_return: 'Quên trả xe',
} as const;
export type DriverPayType = keyof typeof DRIVER_PAY_LABELS;

export interface DriverPayItem {
  type: DriverPayType;
  label: string;
  /** VND, khoản trừ mang số âm */
  amount: number;
}

/** Tiền theo phút × đơn giá/giờ, làm tròn đồng (ROUND_HALF_UP). */
export const moneyPerHour = (minutes: number, ratePerHour: number) =>
  new Decimal(minutes).mul(ratePerHour).div(60).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();

export function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const rad = (d: number) => (d * Math.PI) / 180;
  const a = Math.sin(rad(lat2 - lat1) / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lng2 - lng1) / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Chấm vào có ở bãi xe không. Chấm qua WiFi công ty coi như ở bãi. Bãi riêng (vĩ độ / kinh độ) nếu khai; không thì các nơi làm việc có tọa độ; không có gì → coi như ở bãi (như CRM). */
export function isAtDepot(p: DriverPolicy, lat: number | null, lng: number | null, onCompanyWifi = false): boolean {
  if (!p.requireDepotForOt || onCompanyWifi) return true;
  if (p.depotLat !== null && p.depotLng !== null) {
    if (lat === null || lng === null) return false;
    return haversineMeters(lat, lng, p.depotLat, p.depotLng) <= p.depotRadiusM;
  }
  // DEC-388: bãi xe = các nơi làm việc (3 cơ sở); chưa nơi nào có tọa độ thì không kiểm (như CRM)
  const sites = p.sites ?? [];
  if (!sites.length) return true;
  if (lat === null || lng === null) return false;
  return sites.some((s) => haversineMeters(lat, lng, s.lat, s.lng) <= s.radiusM);
}

export type DriverCheckInStatus = 'before_open' | 'early_ot' | 'on_time' | 'late' | 'closed';

/** Phân loại giờ vào: ≤ earlyOtUntil ở bãi = tăng ca sáng; ≤ onTimeUntil đúng giờ; ≤ gateInClose trễ; sau đó đóng cổng. */
export function classifyDriverCheckIn(p: DriverPolicy, minuteOfDay: number, atDepot: boolean): { status: DriverCheckInStatus; lateMinutes: number } {
  if (minuteOfDay < timeToMin(p.gateInOpen)) return { status: 'before_open', lateMinutes: 0 };
  if (minuteOfDay <= timeToMin(p.earlyOtUntil)) return { status: atDepot ? 'early_ot' : 'on_time', lateMinutes: 0 };
  const onTime = timeToMin(p.onTimeUntil);
  if (minuteOfDay <= onTime) return { status: 'on_time', lateMinutes: 0 };
  if (minuteOfDay <= timeToMin(p.gateInClose)) return { status: 'late', lateMinutes: minuteOfDay - onTime };
  return { status: 'closed', lateMinutes: 0 };
}

/** Công 1 ngày tài xế: ra trong 15 phút cuối ca hoặc làm ≥ 50% ca → 1; còn lại 0,5; thiếu vào/ra → 0. */
export function driverDayWork(p: DriverPolicy, inMin: number | null, outMin: number | null): number {
  if (inMin === null || outMin === null) return 0;
  const start = timeToMin(p.dayStart);
  const end = timeToMin(p.dayEnd);
  const actualIn = Math.max(inMin, start);
  if (actualIn >= end || outMin <= start || end <= start) return 0;
  if (outMin >= end - 15) return 1;
  return (outMin - actualIn) / (end - start) >= 0.5 ? 1 : 0.5;
}

export interface DriverDayInput {
  checkIn: string | null;
  /** Giờ trả xe; trả sau nửa đêm (≤ shiftMaxNextDay hôm sau) vẫn tính vào ngày nhận xe */
  checkOut: string | null;
  latIn: number | null;
  lngIn: number | null;
  wifiIn: boolean;
  dayFraction: number | null;
  /** Vai trò trên xe trong ngày; null = không gán xe */
  role: 'primary' | 'assistant' | null;
  /** Km GPS xe chạy trong khung trưa; null = không có số GPS */
  lunchKm: number | null;
  /**
   * Phút (từ 0h ngày nhận xe, có thể > 1440) các phiếu THUỘC LỆNH GIAO SAU 17H hợp lệ (GPS ở chỗ khách) đã giao xong.
   * PR5 (4.4): không có lệnh thì không tính tăng ca sau 17h / hỗ trợ đêm (tự chậm không lệnh).
   */
  deliveredMinutes: number[];
  /** Km GPS xe chạy từ giờ vào tới hạn tăng ca sáng; null/thiếu = không có số GPS (không xét điều kiện km) */
  earlyKm?: number | null;
  /** 4.3: có lệnh giao trong ngày (phiếu giao / quản lý điều xe). false = không tính tăng ca sáng; thiếu = không xét */
  hasOrder?: boolean;
  /** 4.4: phút muộn nhất được tính là giờ về (giao xong phiếu cuối + chạy về × 1,3 + 30'); null = không cắt */
  maxReturnMinute?: number | null;
  /** 4.4: quản lý đã duyệt phần giờ về dư (có lý do) → không cắt */
  excessApproved?: boolean;
  /** DEC-767: giờ ra là mốc "Giờ về bãi" người duyệt ghi khi duyệt Xuyên đêm / Về trễ → tính như có lệnh giao sau 17h */
  markReturn?: boolean;
  /** Ngày công 'YYYY-MM-DD' (để biết giờ ra sang hôm sau khi không có giờ vào) */
  workDate?: string;
  /** 4.5: ngày có chuyến lấy hàng "Ở lại qua đêm" → không cộng hỗ trợ đêm */
  overnightPickup?: boolean;
  /** 4.7: ngày trên chuyến tỉnh: start = ngày đi (vẫn tăng ca sáng), middle = giữa chuyến, end = ngày về (vẫn hỗ trợ đêm) */
  province?: 'start' | 'middle' | 'end' | 'single' | null;
  /** 4.8: khoản trừ quên trả xe của ngày (đã tính theo số lần trong tháng) */
  forgotPenalty?: number;
}

export interface DriverDayResult {
  work: number;
  lateMinutes: number;
  status: DriverCheckInStatus | null;
  items: DriverPayItem[];
  /** Phút tăng ca sau 17h đã tính (sau làm tròn) */
  otMinutes: number;
  /** Phút bị cắt vì về muộn hơn giờ về tối đa (chờ quản lý duyệt) */
  excessMinutes: number;
  /** Tổng phút tăng ca (sáng + sau 17h + sau 0h) – cảnh báo vượt otWarnHours/tháng */
  otTotalMinutes: number;
}

const item = (type: DriverPayType, amount: number): DriverPayItem => ({ type, label: DRIVER_PAY_LABELS[type], amount });

/**
 * Tiền 1 ca tài xế theo quy tắc PR5 (bảng 4.2):
 *  - Tăng ca sáng: nhận xe ở bãi trước 07:00, có lệnh giao, xe chạy ≥ earlyOtMinKm → theo giờ thực × 60.000.
 *  - Trễ sau 07:15: trừ theo giờ. Xuyên trưa ≥ 25 km: khoán.
 *  - Có lệnh giao sau 17h (phiếu giao xong ≥ 16:30): tăng ca 17:00 → min(trả xe, giờ về tối đa, 21:00), tròn xuống 15'.
 *  - Trả xe từ 21:00: hỗ trợ đêm (phụ xe mức riêng), không cộng khi có "Ở lại qua đêm"; sau 0h mỗi giờ 60.000 tới 04:00.
 */
export function computeDriverDay(p: DriverPolicy, d: DriverDayInput, rules: DriverRuleParams = DEFAULT_DRIVER_RULES): DriverDayResult {
  const inMin = d.checkIn ? vnMinuteOfDay(d.checkIn) : null;
  let outMin = d.checkOut ? vnMinuteOfDay(d.checkOut) : null;
  // Ra qua nửa đêm: cộng theo số ngày lệch giữa ngày vào và ngày ra (giờ VN). Trước đây chỉ cộng khi giờ ra < giờ vào
  // → vào 07:07, ra 08:18 sáng hôm sau bị hiểu là ra 08:18 cùng ngày, 0,5 công (Nguyễn Quốc Khái 02/11/2026, DEC-766).
  const baseDate = d.checkIn ? vnDateOf(d.checkIn) : (d.workDate ?? null);
  if (outMin !== null && d.checkOut && baseDate) {
    const days = Math.round((Date.parse(`${vnDateOf(d.checkOut)}T00:00:00Z`) - Date.parse(`${baseDate}T00:00:00Z`)) / 86_400_000);
    if (days > 0) outMin += 1440 * days;
    else if (inMin !== null && outMin < inMin) outMin += 1440;
  }
  const items: DriverPayItem[] = [];
  let status: DriverCheckInStatus | null = null;
  let lateMinutes = 0;
  let otMinutes = 0;
  let excessMinutes = 0;
  let extraMinutes = 0;
  const midTrip = d.province === 'middle';

  // 1. Tăng ca sáng: vào ở bãi xe trước earlyOtUntil, sớm ít nhất earlyOtMinMinutes, có lệnh giao
  if (inMin !== null) {
    const c = classifyDriverCheckIn(p, inMin, isAtDepot(p, d.latIn, d.lngIn, d.wifiIn));
    status = c.status;
    lateMinutes = c.lateMinutes;
    const early = timeToMin(p.earlyOtUntil) - inMin;
    // Có số GPS mà xe chạy chưa đủ km tối thiểu → chỉ mở app chấm công, không tính tăng ca (minhtiencrm)
    const kmOk = d.earlyKm === null || d.earlyKm === undefined || d.earlyKm >= (p.earlyOtMinKm ?? 0);
    const orderOk = d.hasOrder !== false;
    const noEarly = midTrip || d.province === 'end';
    if (!noEarly && c.status === 'early_ot' && kmOk && orderOk && early >= p.earlyOtMinMinutes && early > 0) {
      items.push(item('early', moneyPerHour(early, p.earlyOtRate)));
      extraMinutes += early;
    }
    if (!noEarly && lateMinutes > 0) items.push(item('late', -moneyPerHour(lateMinutes, p.latePenaltyRate)));
  }

  // 2. Chạy xuyên trưa: km GPS trong khung trưa ≥ lunchMinKm (tài xế chính, không tính giữa chuyến tỉnh)
  if (d.role === 'primary' && !midTrip && d.province !== 'start' && d.lunchKm !== null && d.lunchKm > 0 && d.lunchKm >= p.lunchMinKm) {
    items.push(item('lunch', p.lunchRate));
  }

  // 3. Sau 17h theo lệnh giao + hỗ trợ đêm + sau 0h
  // DEC-762: tăng ca từ mốc muộn hơn giữa chính sách và quy tắc (từ 01/11/2026: 17:30)
  const a17 = Math.max(timeToMin(p.after17Start), timeToMin(rules.after17OtFrom));
  const minDelivered = timeToMin(rules.after17MinDeliveredAt);
  const hasAfter17 = d.deliveredMinutes.some((m) => m >= minDelivered);
  const provinceEnd = d.province === 'end';
  // DEC-767: người duyệt đã duyệt Xuyên đêm / Về trễ kèm giờ về bãi = xác nhận có chạy tối, tính như có lệnh giao sau 17h
  if (d.role && outMin !== null && !midTrip && d.province !== 'start' && (hasAfter17 || provinceEnd || d.markReturn)) {
    let end = outMin;
    if (!provinceEnd && d.maxReturnMinute !== null && d.maxReturnMinute !== undefined && end > d.maxReturnMinute) {
      if (d.excessApproved) end = outMin;
      else {
        excessMinutes = end - d.maxReturnMinute;
        end = d.maxReturnMinute;
      }
    }
    const maxNext = 1440 + timeToMin(rules.shiftMaxNextDay);
    end = Math.min(end, maxNext);
    const nightStart = timeToMin(p.nightStart);
    // DEC-762: bật hỗ trợ đêm theo mức thì tăng ca chạy tới mốc mức 1 (22:00), không dừng ở 21:00
    const otUntil = nightTiersOn(rules) ? timeToMin(rules.nightTier1From) : nightStart;
    if (d.role === 'primary' && !provinceEnd) {
      otMinutes = floorTo(Math.min(end, otUntil) - a17, rules.otRoundMinutes);
      if (otMinutes > 0) items.push(item('after17', moneyPerHour(otMinutes, p.after17Rate)));
    }
    if (nightTiersOn(rules)) {
      // DEC-761: hỗ trợ đêm theo mức giờ xe về bãi (thay hỗ trợ đêm 1 mức + tiền giờ sau 0h)
      const t = d.overnightPickup ? null : nightTierPay(end, rules, d.role);
      if (t && t.amount > 0) items.push(item(d.role === 'primary' ? 'night' : 'night_assistant', t.amount));
      if (end > 1440) extraMinutes += floorTo(end - 1440, rules.otRoundMinutes);
    } else if (end >= nightStart && !d.overnightPickup) {
      items.push(d.role === 'primary' ? item('night', p.nightRate) : item('night_assistant', p.nightAssistantRate));
      if (d.role === 'primary' && end > 1440) {
        const until = 1440 + timeToMin(rules.afterMidnightUntil);
        const mins = floorTo(Math.min(end, until) - 1440, rules.otRoundMinutes);
        if (mins > 0) {
          items.push(item('after_midnight', moneyPerHour(mins, rules.afterMidnightRate)));
          extraMinutes += mins;
        }
      }
    }
  }
  if (d.forgotPenalty && d.forgotPenalty > 0) items.push(item('forgot_return', -d.forgotPenalty));

  let work = driverDayWork(p, inMin, outMin);
  if (d.province) work = 1;
  if (d.dayFraction !== null) work = d.dayFraction;
  return { work, lateMinutes, status, items, otMinutes, excessMinutes, otTotalMinutes: otMinutes + extraMinutes };
}
